import { FollowWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button, EmptyState, LoadingSpinner, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, RefreshControl, Text, View } from 'react-native';
import { createStyles } from './FollowersScreen.styles';

export interface FollowersScreenProps {
  followers: FollowWithUser[];
  loading: boolean;
  loadingMore: boolean;
  currentUserId?: string;
  followingIds: Set<string>;
  onFollowToggle: (followerId: string) => void;
  onUserPress: (followerId: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
  };
}

export const FollowersScreen = ({
  followers,
  loading,
  loadingMore,
  currentUserId,
  followingIds,
  onFollowToggle,
  onUserPress,
  onRefresh,
  onLoadMore,
  colors,
}: FollowersScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <Text style={[styles.title, { color: colors.text }]}>{t('navigation.followers')}</Text>
        </View>
        <LoadingSpinner />
      </View>
    );
  }

  const renderFollowerItem = ({ item }: { item: FollowWithUser }) => {
    const isCurrentUser = item.followerId === currentUserId;
    const isFollowingBack = followingIds.has(item.followerId);

    return (
      <UserCard
        userId={item.followerId}
        userName={item.userName}
        userEmail={item.userEmail}
        userAvatarUrl={item.userAvatarUrl}
        onPress={() => onUserPress(item.followerId)}
        actions={
          !isCurrentUser ? (
            <Button
              title={isFollowingBack ? t('social.unfollow') : t('social.follow')}
              onPress={() => onFollowToggle(item.followerId)}
              variant={isFollowingBack ? 'secondary' : 'primary'}
              size="small"
              colors={colors}
            />
          ) : null
        }
        colors={colors}
      />
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.title, { color: colors.text }]}>{t('navigation.followers')}</Text>
        <Text style={[styles.count, { color: colors.textSecondary }]}>
          {followers.length} {followers.length === 1 ? t('followers.follower') : t('followers.followers')}
        </Text>
      </View>

      <FlatList
        data={followers}
        renderItem={renderFollowerItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
        }
        ListFooterComponent={loadingMore ? <LoadingSpinner /> : null}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="people-outline" size={52} color={colors.textTertiary} />}
            title={t('followers.noFollowersYet')}
            subtitle={t('followers.whenFollowAppear')}
            colors={colors}
          />
        }
      />
    </View>
  );
};
